import React from 'react';
import { Calculator, TrendingUp, TrendingDown, Percent, CalendarDays, FileText } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета налога на прибыль
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  return (
    <ToolResult title="Результаты расчета налога на прибыль">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
        {/* Налогооблагаемая база */}
        <div className="rounded-lg bg-gradient-to-br from-green-50 to-teal-50 dark:from-green-900/30 dark:to-teal-900/30 p-5 border border-green-100 dark:border-green-800/50 shadow-sm">
          <h3 className="text-base font-medium text-green-700 dark:text-green-300 mb-1.5 flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4"/>
            Налогооблагаемая база
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-green-800 dark:text-green-200" aria-live="polite">
              {formatCurrency(results.taxableIncome)}
            </span>
          </div>
          <p className="text-xs text-green-600 dark:text-green-400 mt-1">
            Доходы минус расходы и применимые вычеты
          </p>
        </div>

        {/* Итоговый налог */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/30 dark:to-blue-900/30 p-5 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
          <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
            <Calculator className="w-4 h-4"/>
            Итоговый налог к уплате
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-indigo-800 dark:text-indigo-200" aria-live="polite">
              {formatCurrency(results.totalTaxAmount)}
            </span>
          </div>
          <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
            Сумма налога по всем видам деятельности
          </p>
        </div>

        {/* Эффективная ставка */}
        <div className="rounded-lg bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/30 dark:to-yellow-900/30 p-5 border border-amber-100 dark:border-amber-800/50 shadow-sm">
          <h3 className="text-base font-medium text-amber-700 dark:text-amber-300 mb-1.5 flex items-center gap-1.5">
            <Percent className="w-4 h-4"/>
            Эффективная ставка налога
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-amber-800 dark:text-amber-200" aria-live="polite">
              {formatNumber(results.effectiveTaxRate, 2)}%
            </span>
          </div>
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
            Процент налога относительно общего дохода
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Налоговый период" 
          value={results.taxationYear} 
          color="indigo" 
          icon={<CalendarDays className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Общая сумма доходов" 
          value={formatCurrency(results.totalIncome)} 
          color="green" 
          icon={<TrendingUp className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Общая сумма расходов" 
          value={formatCurrency(results.totalExpenses)} 
          color="red" 
          icon={<TrendingDown className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Налогооблагаемая база" 
          value={formatCurrency(results.taxableIncome)} 
          color="green"
          icon={<Calculator className="w-4 h-4"/>} 
        />

        <ResultItem 
          label="Ставка налога на прибыль" 
          value={`${results.baseTaxRate}% (${results.federalRate}% фед. + ${results.regionalRate}% рег.)`} 
          color="indigo"
          icon={<Percent className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Налог в федеральный бюджет" 
          value={formatCurrency(results.incomeTaxFederal)} 
          color="indigo"
        />
        
        <ResultItem 
          label="Налог в региональный бюджет" 
          value={formatCurrency(results.incomeTaxRegional)} 
          color="indigo"
        />
        
        {/* Отображаем налог на дивиденды, если он рассчитан */}
        {results.dividendTaxAmount > 0 && (
          <ResultItem 
            label="Налог с дивидендов" 
            value={formatCurrency(results.dividendTaxAmount)} 
            color="amber"
            icon={<FileText className="w-4 h-4"/>} 
          />
        )}
        
        {/* Отображаем специальные вычеты, если они применены */}
        {results.specialDeductionsTotal > 0 && (
          <ResultItem 
            label="Применено специальных вычетов" 
            value={formatCurrency(results.specialDeductionsTotal)} 
            color="blue"
          />
        )}
        
        {/* Отображаем убытки прошлых лет, если они применены */}
        {results.lossesApplied > 0 && (
          <ResultItem 
            label="Учтено убытков прошлых лет" 
            value={formatCurrency(results.lossesApplied)} 
            color="blue"
          />
        )}

        <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
          <ResultItem 
            label="Итого налог к уплате" 
            value={formatCurrency(results.totalTaxAmount)} 
            bold={true}
            large={true}
            color="indigo"
          />
        </div>
      </div>

      {/* Информационный блок */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-6 border border-gray-200 dark:border-gray-700">
        <div className="flex items-start gap-3">
          <div className="mt-0.5">
            <FileText className="w-5 h-5 text-indigo-500" />
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Сроки уплаты налога на прибыль</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Организации обязаны уплатить налог на прибыль не позднее 28 марта года, следующего за отчетным. 
              Авансовые платежи уплачиваются не позднее 28 числа месяца, следующего за отчетным периодом.
            </p>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;